import {skipToken} from '@reduxjs/toolkit/query';

import {isNil, isNotNil, uniqBy} from 'ramda';

import {
  SourcingUserListQuery,
  AuctionVehicleDetailResponseBody,
  getAuctionId,
  useGetSourcingUserListInfiniteQuery,
  useGetAuctionUserListInfiniteQuery,
} from '@dms/api/core';

import {SourcingTableQueryResult} from '../types/SourcingTableQueryResult';
import {adaptAuctionVehicleToSourcingItem} from '../utils/adaptAuctionVehicleToSourcingItem';
import {SourcingType} from './useSourcingType';

const EMPTY_ENUM_LOOKUP = new Map<string, string>();

function uniqueAuctionVehicles(vehicles: AuctionVehicleDetailResponseBody[]) {
  const seenIds = new Set<string>();
  return vehicles.filter((vehicle) => {
    const id = getAuctionId(vehicle);
    if (isNil(id) || id === '') {
      return true;
    }
    if (seenIds.has(id)) {
      return false;
    }
    seenIds.add(id);
    return true;
  });
}

type UsePaginatedUserListProps = {
  query: SourcingUserListQuery;
  sourcingType: SourcingType;
  enumLookup?: ReadonlyMap<string, string>;
};

export function usePaginatedUserList(props: UsePaginatedUserListProps) {
  const classifieds = useGetSourcingUserListInfiniteQuery(props.query, {
    skip: props.sourcingType !== SourcingType.CLASSIFIEDS,
  });
  const auctions = useGetAuctionUserListInfiniteQuery(
    props.sourcingType === SourcingType.AUCTIONS && props.query.list !== 'user-carvago-buy'
      ? {...props.query, list: props.query.list, orderBy: props.query.orderBy?.split(',')}
      : skipToken
  );
  const activeQuery = props.sourcingType === SourcingType.CLASSIFIEDS ? classifieds : auctions;
  const auctionVehicles = uniqueAuctionVehicles(
    auctions.currentData?.pages.flatMap((page) => page.data) ?? []
  );
  const rows =
    props.sourcingType === SourcingType.CLASSIFIEDS
      ? (classifieds.currentData?.pages.flatMap((page) => page.data) ?? [])
      : auctionVehicles.map((item) =>
          adaptAuctionVehicleToSourcingItem(item, props.enumLookup ?? EMPTY_ENUM_LOOKUP)
        );
  const data =
    props.sourcingType === SourcingType.CLASSIFIEDS
      ? uniqBy((row) => row.sourcingVehicle.adId, rows)
      : rows;
  const lastPage = activeQuery.currentData?.pages.at(-1);
  const loadMore = () => {
    if (activeQuery.hasNextPage && !activeQuery.isFetching && !activeQuery.isError) {
      void activeQuery.fetchNextPage();
    }
  };
  const queryResult = {
    data,
    count: lastPage?.links.totalCount,
    isLoading: activeQuery.isLoading || (isNil(activeQuery.currentData) && activeQuery.isFetching),
    isFetching: activeQuery.isFetching,
    isFetchingNextPage: activeQuery.isFetchingNextPage,
    error: activeQuery.error,
    isEndReached: isNotNil(lastPage) && !activeQuery.hasNextPage,
    onLoadMore: loadMore,
    onRetry: () => {
      void (activeQuery.isFetchNextPageError ? activeQuery.fetchNextPage() : activeQuery.refetch());
    },
  } satisfies SourcingTableQueryResult;
  return {
    queryResult,
    isTerminalEmpty:
      !queryResult.isLoading &&
      isNil(queryResult.error) &&
      queryResult.isEndReached &&
      data.length === 0,
    auctionVehicles,
    fulfilledTimeStamp: activeQuery.fulfilledTimeStamp,
    listQueryKey: JSON.stringify([props.sourcingType, props.query]),
  };
}
